import { __decorate } from "tslib";
import { attr, customElement, FASTElement, html } from '@microsoft/fast-element';
import { ChameleonElementMixin } from '../common/ChameleonElement';
import styles from './InputStepper.styles.scss';
import { SVG_ADD, SVG_SUBTRACT } from '@getgo/chameleon-icons';
const template = html `<template>
  <chameleon-number-field
    error=${(x) => x.error}
    :value=${(x) => x.value}
    @change=${(x, { event }) => x.handleChange(event)}
    fieldsize="${(x) => x.size}"
    size="${(x) => x.value.toString().length}"
  >
    <chameleon-icon-button
      size="${(x) => x.iconButtonSize()}"
      slot="start"
      aria-label=${(x) => x.decrementLabel}
      tabindex="-1"
      @click=${(x) => x.decrement()}
      ?disabled=${(x) => x.cantGoLower()}
    >
      <chameleon-svg>${SVG_SUBTRACT}</chameleon-svg>
    </chameleon-icon-button>
    <chameleon-icon-button
      size="${(x) => x.iconButtonSize()}"
      aria-label=${(x) => x.incrementLabel}
      tabindex="-1"
      ?disabled=${(x) => x.cantGoHigher()}
      slot="end"
      @click=${(x) => x.increment()}
    >
      <chameleon-svg>${SVG_ADD}</chameleon-svg>
    </chameleon-icon-button>
    <span slot="helpertext">${(x) => x.helpertext}</span>
    <slot></slot>
  </chameleon-number-field>
</template>`;
export const inputStepperDefinition = {
    name: 'chameleon-input-stepper',
    template,
    styles,
};
let InputStepperComponent = class InputStepperComponent extends ChameleonElementMixin(FASTElement) {
    constructor() {
        super(...arguments);
        this.size = 'medium';
        this.value = 0;
        this.step = 1;
        this.min = 0;
        this.max = Number.MAX_VALUE;
        this.helpertext = '';
        this.incrementLabel = '';
        this.decrementLabel = '';
        this.availableKeys = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete'];
        this.handleKeyDown = (event) => {
            const { key } = event;
            if (key === 'ArrowUp') {
                this.increment();
                event.preventDefault();
            }
            else if (key === 'ArrowDown') {
                this.decrement();
                event.preventDefault();
            }
            else if (this.isKeyNotAllowed(key)) {
                event.preventDefault();
            }
        };
    }
    isKeyNotAllowed(key) {
        return isNaN(Number(key)) && !this.availableKeys.find((availableKey) => availableKey === key);
    }
    sanitizeValue() {
        this.value = Number(this.value);
    }
    connectedCallback() {
        super.connectedCallback();
        this.addEventListener('keydown', this.handleKeyDown);
    }
    disconnectedCallback() {
        this.removeEventListener('keydown', this.handleKeyDown);
    }
    cantGoLower() {
        return this.value <= Number(this.min);
    }
    cantGoHigher() {
        return this.value >= Number(this.max);
    }
    increment() {
        if (this.cantGoHigher()) {
            return;
        }
        this.sanitizeValue();
        this.value += Number(this.step);
        this.$emit('change', null, { bubbles: false });
        if (this.value > this.max) {
            this.value = this.max;
        }
    }
    decrement() {
        if (this.cantGoLower()) {
            return;
        }
        this.sanitizeValue();
        this.value -= Number(this.step);
        this.$emit('change', null, { bubbles: false });
        if (this.value < this.min) {
            this.value = this.min;
        }
    }
    handleChange(event) {
        this.sanitizeValue();
        this.value = event.target.value;
        this.$emit('change', null, { bubbles: false });
    }
    iconButtonSize() {
        return this.size === 'medium' ? 'small' : 'xsmall';
    }
};
__decorate([
    attr
], InputStepperComponent.prototype, "size", void 0);
__decorate([
    attr
], InputStepperComponent.prototype, "value", void 0);
__decorate([
    attr
], InputStepperComponent.prototype, "step", void 0);
__decorate([
    attr
], InputStepperComponent.prototype, "min", void 0);
__decorate([
    attr
], InputStepperComponent.prototype, "max", void 0);
__decorate([
    attr({ mode: 'boolean' })
], InputStepperComponent.prototype, "error", void 0);
__decorate([
    attr
], InputStepperComponent.prototype, "helpertext", void 0);
__decorate([
    attr({ attribute: 'decrement-aria-label' })
], InputStepperComponent.prototype, "incrementLabel", void 0);
__decorate([
    attr({ attribute: 'increment-aria-label' })
], InputStepperComponent.prototype, "decrementLabel", void 0);
InputStepperComponent = __decorate([
    customElement(inputStepperDefinition)
], InputStepperComponent);
export { InputStepperComponent };
